import { createFromIconfontCN } from '@ant-design/icons';
import { AccountInfo, SilentRequest } from '@azure/msal-browser';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { Menu } from 'antd';
import jwtDecode from 'jwt-decode';
import { FunctionComponent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { loginRequest, Scopes } from '../../auth/authConfig';
import { getToken, resetToken, setToken, isAuthDisabled } from '../../auth/UserService';
import { useLocalAuth, isLocallyAuthenticated, localLogout, getLocalUser, fetchAndCacheMsalRole } from '../../auth/LocalAuthService';
import { Actions } from '../../interfaces/general/general';
import PermissionService from '../../services/Permissions';
import { RolesEnum } from '../../constants/roles';

interface SlideInMenuProps {
    setIsShown: Actions;
    isShown: boolean;
}

const HEADER_HEIGHT_PX = 82;

const SlideInMenu: FunctionComponent<SlideInMenuProps> = ({ setIsShown, isShown }) => {
    const { t } = useTranslation(['f', 'b']),
        { instance, accounts } = useMsal(),
        [name, setName] = useState<string | undefined>(''),
        [, setMsalRoleTick] = useState(0),
        history = useHistory();

    const IconFont = createFromIconfontCN({
        scriptUrl: '//at.alicdn.com/t/font_8d5l8fzk5b87iudi.js',
    });

    const localAuth = useLocalAuth();
    const localUser = getLocalUser();
    const hasLocalSession = isLocallyAuthenticated();
    const localRole = Number(localUser?.role ?? -1);
    const isLocalAdminRole = localRole === RolesEnum.Admin || localRole === RolesEnum.SuperAdmin;
    const isAdminUser = hasLocalSession
        ? (localRole === RolesEnum.Admin || localRole === RolesEnum.SuperAdmin)
        : (!localAuth && PermissionService.canViewAdminPanelMenu());

    const canShowSettings = hasLocalSession
        ? (localRole === RolesEnum.Admin || localRole === RolesEnum.SuperAdmin)
        : (!localAuth && PermissionService.canViewAdminPanelMenu());

    const isDealRoomOnlyUser = (localAuth && isLocallyAuthenticated())
        ? !isLocalAdminRole
        : PermissionService.isDealRoomOnlyMsalUser();

    const canShowFullMenu = !isDealRoomOnlyUser;

    const handleClientLogin = () => {
        if (localAuth) {
            history.push('/login');
            setIsShown.setFalse();
            return;
        }

        instance.loginRedirect(loginRequest);
    };

    const handleAdminLogin = () => {
        if (localAuth) {
            history.push('/login');
            setIsShown.setFalse();
            return;
        }

        instance.loginRedirect(loginRequest);
    };

    const handleSignout = () => {
        if (localAuth) {
            localLogout();
            resetToken();
            history.push('/login');
            setIsShown.setFalse();
            return;
        }
        instance.logout();
        resetToken();
    };

    const fetchToken = async () => {
        let account = accounts.length ? (accounts[0] as AccountInfo) : null;
        if (account) {
            localLogout(); // clear stale local-auth session when MSAL account is active
            setName(account.name ?? account.username);
            let request: SilentRequest = {
                scopes: Scopes.api,
                account: account,
            };
            try {
                var authenticationResult = await instance.acquireTokenSilent(request);
                setToken(authenticationResult.accessToken);
                await fetchAndCacheMsalRole();
                setMsalRoleTick(n => n + 1);
            } catch (error: any) {
                instance.acquireTokenRedirect(request);
            }
        }
    };

    const isSuperAdmin = useCallback(() => {
        if (localAuth) {
            const localRole = Number(localUser?.role ?? -1);
            return localRole === RolesEnum.SuperAdmin;
        }

        const token = getToken();
        if (token) {
            const decode: any = jwtDecode(token);

            return decode['role'] == '3' ? true : false;
        } else {
            return false;
        }
    }, []);

    useEffect(() => {
        async function fetch() {
            if (accounts && accounts.length > 0) {
                await fetchToken();
            } else {
                // resetToken();
            }
        }

        fetch();
    }, [accounts]);

    const handleMenuLinkClick = (e: MouseEvent<HTMLAnchorElement>, path: string) => {
        const isPlainLeftClick =
            e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

        // Let browser handle middle-click, right-click context menu and modified clicks.
        if (!isPlainLeftClick) {
            return;
        }

        e.preventDefault();
        history.push(path);
        setIsShown.setFalse();
    };

    const authSection = (
        <div style={{ paddingBottom: 30 }}>
            {localAuth ? (
                isLocallyAuthenticated() ? (
                    <div style={{ height: 55, display: 'flex', alignItems: 'center' }} onClick={handleSignout}>
                        <span className="slide-in__text ml-40">{t('f:menu.signout')}</span>
                    </div>
                ) : (
                    <>
                        <div style={{ height: 55, display: 'flex', alignItems: 'center' }} onClick={handleClientLogin}>
                            <span className="slide-in__text ml-20">{t('f:menu.clientLogin')}</span>
                        </div>
                        <div style={{ height: 55, display: 'flex', alignItems: 'center' }} onClick={handleAdminLogin}>
                            <span className="slide-in__text ml-20">{t('f:menu.adminLogin')}</span>
                        </div>
                    </>
                )
            ) : (
                <>
                    <UnauthenticatedTemplate>
                        <div style={{ height: 55, display: 'flex', alignItems: 'center' }} onClick={handleClientLogin}>
                            <span className="slide-in__text ml-20">{t('f:menu.clientLogin')}</span>
                        </div>
                        <div style={{ height: 55, display: 'flex', alignItems: 'center' }} onClick={handleAdminLogin}>
                            <span className="slide-in__text ml-20">{t('f:menu.adminLogin')}</span>
                        </div>
                    </UnauthenticatedTemplate>
                    <AuthenticatedTemplate>
                        <div style={{ height: 55, display: 'flex', alignItems: 'center' }} onClick={handleSignout}>
                            <span className="slide-in__text ml-40">{t('f:menu.signout')}</span>
                        </div>
                    </AuthenticatedTemplate>
                </>
            )}
        </div>
    );

    if (!isShown) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: HEADER_HEIGHT_PX,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                zIndex: 9999,
            }}
        >
            <div className="slide-in__container d-flex flex-direction-column" style={{ flexShrink: 0 }}>
                {canShowFullMenu ? (
                    <>
                        <Menu
                            defaultSelectedKeys={['1']}
                            mode="inline"
                            inlineCollapsed={false}
                            style={{ flex: 1, background: 'transparent', border: 'none' }}
                        >
                            <Menu.Item key="main-overview">
                                <a href="/data-link-or-transaction" onClick={(e) => handleMenuLinkClick(e, '/data-link-or-transaction')}>
                                    <span className="slide-in__text ml-40">{t('f:menu.overview')}</span>
                                </a>
                            </Menu.Item>
                            <Menu.Item key="main-transaction">
                                <a href="/overview" onClick={(e) => handleMenuLinkClick(e, '/overview')}>
                                    <span className="slide-in__text ml-40">{t('f:menu.transaction')}</span>
                                </a>
                            </Menu.Item>
                            <Menu.Item key="main-search-property">
                                <a href="/search-property" onClick={(e) => handleMenuLinkClick(e, '/search-property')}>
                                    <span className="slide-in__text ml-40">Search Property</span>
                                </a>
                            </Menu.Item>
                            <Menu.Item key="main-building-directory">
                                <a href="/building-directory" onClick={(e) => handleMenuLinkClick(e, '/building-directory')}>
                                    <span className="slide-in__text ml-40">Building Directory</span>
                                </a>
                            </Menu.Item>
                            <Menu.Item key="main-documents">
                                <a href="/documents" onClick={(e) => handleMenuLinkClick(e, '/documents')}>
                                    <span className="slide-in__text ml-40">{t('f:menu.documents')}</span>
                                </a>
                            </Menu.Item>
                            <Menu.Item key="main-transaction-data">
                                <a href="/transaction-data" onClick={(e) => handleMenuLinkClick(e, '/transaction-data')}>
                                    <span className="slide-in__text ml-40">Transaction Data</span>
                                </a>
                            </Menu.Item>
                            <Menu.Item key="main-trade-probability">
                                <a href="/trade-probability" onClick={(e) => handleMenuLinkClick(e, '/trade-probability')}>
                                    <span className="slide-in__text ml-40">Trade Probability</span>
                                </a>
                            </Menu.Item>
                            {isSuperAdmin() && (
                                <Menu.Item key="4-analytics">
                                    <a href="/data-analytics" onClick={(e) => handleMenuLinkClick(e, '/data-analytics')}>
                                        <span className="slide-in__text ml-40">{t('f:menu.analytics')}</span>
                                    </a>
                                </Menu.Item>
                            )}
                            {canShowSettings && (
                                <>
                                    <Menu.Item key="main-settings-header" disabled>
                                        <span className="slide-in__text ml-40">Settings</span>
                                    </Menu.Item>
                                    <Menu.Item key="main-documents-settings">
                                        <a href="/manage-documents" onClick={(e) => handleMenuLinkClick(e, '/manage-documents')}>
                                            <span className="slide-in__text ml-60">Manage Deal Room</span>
                                        </a>
                                    </Menu.Item>
                                    <Menu.Item key="main-roles-settings">
                                        <a href="/roles/settings" onClick={(e) => handleMenuLinkClick(e, '/roles/settings')}>
                                            <span className="slide-in__text ml-60">Role Management</span>
                                        </a>
                                    </Menu.Item>
                                    <Menu.Item key="main-company-settings">
                                        <a href="/admin-panel/company" onClick={(e) => handleMenuLinkClick(e, '/admin-panel/company')}>
                                            <span className="slide-in__text ml-60">Company Management</span>
                                        </a>
                                    </Menu.Item>
                                    {isSuperAdmin() && (
                                        <Menu.Item key="main-theme-settings">
                                            <a href="/theme" onClick={(e) => handleMenuLinkClick(e, '/theme')}>
                                                <span className="slide-in__text ml-60">{t('f:menu.theme')}</span>
                                            </a>
                                        </Menu.Item>
                                    )}
                                </>
                            )}
                        </Menu>
                        {authSection}
                    </>
                ) : (
                    <>
                        <div style={{ height: '20%' }} />
                        <div style={{ height: 55, display: 'flex', alignItems: 'center' }}>
                            <a href="/documents" onClick={(e) => handleMenuLinkClick(e, '/documents')}>
                                <span className="slide-in__text ml-40">{t('f:menu.documents')}</span>
                            </a>
                        </div>
                        {authSection}
                    </>
                )}
            </div>

            <div
                style={{
                    flex: 1,
                    backgroundColor: 'rgba(15, 36, 47, 0.7)',
                    cursor: 'pointer',
                    minHeight: `calc(100vh - ${HEADER_HEIGHT_PX}px)`,
                }}
                onClick={() => {
                    setIsShown.setFalse();
                }}
            />
        </div>
    );
};
export default SlideInMenu;
